<script>
  import {
    activeSpec,
    systemLog,
    data,
    dataInfo,
    viewSpecs,
    unlockedSpecs,
  } from "../../editor/storage.js";
  import { underHypothesis } from "../../editor/exploration/exploration.js";
  import {
    openSystemTooltip,
    closeSystemTooltip,
  } from "../../editor/utility.js";
  import {
    showSpinnerOnArtboard,
    hideSpinnerOnArtboard,
    moveToArtboard,
    afterRender,
  } from "../../editor/component/artboard.js";

  export let view = false;
  export let spec;
  export let statusBarControl;
  let onHypo = [];
  let currVersion = spec.currVersion;

  activeSpec.subscribe((u) => {
    if (u !== spec?.uid) {
      view = false;
      statusBarControl("versions", view);
    }
  });

  async function hypothesize(i, version) {
    if (!onHypo[i]) {
      underHypothesis.set(true);
      showSpinnerOnArtboard(spec.uid);
      onHypo[i] = true;
      spec
        .hypothesize($data, $dataInfo, version.save, () => {
          afterRender(spec);
        })
        .finally(() => {
          hideSpinnerOnArtboard(spec.uid);
          $systemLog.log({
            message: "Hypothesize a version",
            artboard_uid: spec.uid,
            version_name: version.name,
            category: "version_control",
          });
          underHypothesis.set(false);
          moveToArtboard(spec.uid);
        });
    }
  }

  async function dehypothesize(i) {
    if (onHypo[i]) {
      underHypothesis.set(true);
      onHypo[i] = false;
      showSpinnerOnArtboard(spec.uid);
      spec
        .dehypothesize($data, $dataInfo, () => {
          afterRender(spec);
        })
        .finally(() => {
          hideSpinnerOnArtboard(spec.uid);
          $systemLog.log({
            message: "Dehypothesize a version",
            artboard_uid: spec.uid,
            category: "version_control",
          });
          underHypothesis.set(false);
        });
    }
  }

  async function dehypothesizeAll() {
    onHypo.forEach((d, i) => {
      if (d) {
        dehypothesize(i);
      }
    });
  }

  async function checkout(name) {
    viewSpecs.update((specs) => {
      specs.forEach((s, si) => {
        if (s.uid === spec.uid) {
          s.checkoutVersion(name)
            .render($data, $dataInfo, () => {
              afterRender(s);
            })
            .finally(() => {
              $systemLog.log({
                message: "Checkout an artboard vesrion",
                artboard_id: s.uid,
                version_name: name,
              });
            });
        }
      });
      return specs;
    });
  }

  async function saveVersion() {
    viewSpecs.update((specs) => {
      specs.forEach((s, si) => {
        if (s.uid === spec.uid) {
          let vname = s.saveVersion();
          s.checkoutVersion(vname)
            .render($data, $dataInfo, () => {
              afterRender(s);
            })
            .finally(() => {
              $systemLog.log({
                message: "Saved a version",
                artboard_uid: s.uid,
                version_name: vname,
              });
            });
        }
      });
      return specs;
    });
  }

  $: {
    if (!view) {
      dehypothesizeAll();
    }
  }
  viewSpecs.subscribe((specs) => {
    specs.forEach((s) => {
      if (s.uid === spec.uid) {
        spec = s;
      }
    });
  });
</script>

{#if spec?.versions?.constructor?.name === "Array"}
  <button
    type="button"
    style="text-overflow: ellipsis;overflow: hidden;white-space: nowrap;"
    data-prevent-move="true"
    class={"history" + (view ? " opened" : " closed")}
    on:click={() => {
      if ($unlockedSpecs.includes(spec.uid)) {
        view = !view;
        $systemLog.log({
          message: "Toggle the list of the current artboard versions",
          artboard_uid: spec.uid,
          status: view,
        });
        statusBarControl("versions", view);
      }
    }}
    on:mouseover={(event) => {
      if (!$unlockedSpecs.includes(spec.uid)) {
        openSystemTooltip(
          event,
          "Unlock the artboard to see the version history"
        );
      } else {
        openSystemTooltip(
          event,
          (view ? "Close" : "Open") +
            " version control" +
            (spec.changeAfterCheckout
              ? " (Change(s) made to this version)"
              : " (No change made to this version)")
        );
      }
    }}
    on:focus={(event) => {
      if (!$unlockedSpecs.includes(spec.uid)) {
        openSystemTooltip(
          event,
          "Unlock the artboard to see the version history"
        );
      } else {
        openSystemTooltip(
          event,
          (view ? "Close" : "Open") +
            " version control" +
            (spec.changeAfterCheckout
              ? " (Change(s) made to this version)"
              : " (No change made to this version)")
        );
      }
    }}
    on:mouseout={() => {
      closeSystemTooltip();
    }}
    on:blur={() => {
      closeSystemTooltip();
    }}
    ><i class="bi bi-reply-all-fill me-1" />{spec.currVersion}<sup
      >{spec.changeAfterCheckout ? "*" : ""}</sup
    ></button
  >
  {#if view}
    <div class="history-list" data-prevent-move="true">
      {#if spec.changeAfterCheckout}
        <div class="p-1">
          <button
            class="save-version"
            type="button"
            on:click={() => {
              dehypothesizeAll();
              saveVersion();
            }}
          >
            Save the current version
          </button>
        </div>
      {/if}
      <div class="history-list-wrap">
        {#each spec.versions as v, i}
          <div class="history-list-item">
            <!-- version name -->
            <div class="text-truncate">
              {#if spec.currVersion === v.name}<i
                  class="bi bi-play-fill me-1"
                  alt="Current version"
                />{/if}{v.name}{spec.changeAfterCheckout &&
              spec.currVersion === v.name
                ? "*"
                : ""}{#if v.fromRecommendation}<i
                  class="bi bi-stars ms-1"
                  alt="Recommender-generated version"
                  on:mouseover={(event) => {
                    openSystemTooltip(event, "Recommender-generated version");
                  }}
                  on:focus={(event) => {
                    openSystemTooltip(event, "Recommender-generated version");
                  }}
                  on:mouseout={() => {
                    closeSystemTooltip();
                  }}
                  on:blur={() => {
                    closeSystemTooltip();
                  }}
                />{/if}
            </div>
            <div>
              <!-- preview buttion -->
              {#if spec.currVersion === v.name}
                <button type="button" class="vc-button" disabled>
                  <i
                    class="bi bi-eye"
                    alt="This version is the current view."
                  />
                </button>
              {:else}
                <button
                  type="button"
                  class="vc-button"
                  on:click={() => {
                    if (!onHypo[i]) {
                      dehypothesizeAll();
                      hypothesize(i, v);
                    } else dehypothesize(i);
                  }}
                  on:mouseover={(event) => {
                    openSystemTooltip(event, "Preview this version");
                  }}
                  on:focus={(event) => {
                    openSystemTooltip(event, "Preview this version");
                  }}
                  on:mouseout={() => {
                    closeSystemTooltip();
                  }}
                  on:blur={() => {
                    closeSystemTooltip();
                  }}
                >
                  {#if !onHypo[i]}<i
                      class="bi bi-eye"
                      alt="Preview this version"
                    />{:else}<i
                      class="bi bi-eye-slash-fill"
                      alt="Turn off the preview"
                    />{/if}
                </button>
              {/if}
              <!-- checkout button -->
              {#if spec.currVersion === v.name}
                <button type="button" class="vc-button" disabled>
                  <i
                    class="bi bi-check"
                    alt="This version can't be checked out."
                  />
                </button>
              {:else}
                <button
                  type="button"
                  class="vc-button"
                  on:click={() => {
                    dehypothesizeAll();
                    checkout(v.name).then(() => {
                      moveToArtboard(spec.uid);
                    });
                  }}
                  on:mouseover={(event) => {
                    openSystemTooltip(event, "Check out to this version");
                  }}
                  on:focus={(event) => {
                    openSystemTooltip(event, "Check out to this version");
                  }}
                  on:mouseout={() => {
                    closeSystemTooltip();
                  }}
                  on:blur={() => {
                    closeSystemTooltip();
                  }}
                >
                  <i class="bi bi-check" alt="Check out to this version" />
                </button>
              {/if}
            </div>
          </div>
        {/each}
      </div>
    </div>
  {/if}
{/if}

<style>
  .history {
    appearance: none;
    display: block;
    border: 0;
    background: transparent;
    font-size: 0.75rem;
    padding: 0.15rem 0.5rem;
  }
  .history:hover,
  .history.opened {
    background: rgba(255, 196, 34, 0.6);
  }
  .history-list {
    position: absolute;
    bottom: 0;
    right: -198px;
    width: 190px;
    height: 120px;
    overflow-y: scroll;
    border-radius: var(--default-radius);
    font-size: 0.75rem;
    background-color: #f9f9f9;
    box-shadow: 0 0 3px rgba(0, 0, 0, 0.15);
    border: 1px solid #cccccc;
  }
  .history-list-wrap {
    display: flex;
    flex-direction: column-reverse;
    height: auto;
  }
  .history-list-item {
    display: flex;
    justify-content: space-between;
    flex-wrap: nowrap;
    padding: 0.25rem 0.5rem;
    border-bottom: 1px dashed #dddddd;
  }
  .history-list-item:hover {
    background-color: #fcfcfc;
  }
  .history-list-item:first-of-type {
    border-bottom: 0;
  }
  button.save-version {
    appearance: none;
    display: block;
    width: 100%;
    margin: 0;
    padding: 0.25rem;
    border: 0;
    font-size: 0.8rem;
    background: rgba(0, 0, 0, 0.1);
    border-radius: var(--default-radius);
  }
  button.save-version:hover {
    outline: 1px solid black;
  }
  .vc-button {
    appearance: none;
    border: 0;
    background: transparent;
    font-size: 0.75rem;
    padding: 0;
    margin-right: 0.4rem;
  }
</style>